import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useDiagnosticRooms,
  useDeleteDiagnosticRoom,
  useBulkDeleteDiagnosticRooms,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Floors } from './Floors';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DiagnosticRoomForm } from './DiagnosticRoomForm';
import { toast } from 'sonner';
import {
  DoorOpen,
  Loader2,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  CheckCircle,
  XCircle,
  FileText,
  Building,
} from 'lucide-react';

export function DiagnosticRooms() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = params.get('tab') || 'rooms';

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, error } = useDiagnosticRooms(page, limit, debouncedSearch || undefined);
  const deleteMutation = useDeleteDiagnosticRoom();
  const bulkDeleteMutation = useBulkDeleteDiagnosticRooms();

  const rooms = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState<string | undefined>(undefined);
  const [detailModal, setDetailModal] = useState<any | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rooms.map((room: any) => room.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [rooms]);

  const allSelected = rooms.length > 0 && rooms.every((room: any) => selectedIds.has(room.id));
  const someSelected = rooms.some((room: any) => selectedIds.has(room.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = rooms.length > 0 && rooms.every((room: any) => prev.has(room.id));
      return everySelected ? new Set() : new Set(rooms.map((room: any) => room.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const requestBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setBulkConfirmOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} room${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some rooms');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    newParams.set('tab', tab);
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}?${query}`, { replace: true });
  }, [tab, page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const setTab = (newTab: string) => {
    const newParams = new URLSearchParams();
    newParams.set('tab', newTab);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    setPage(1);
    setSearch('');
    setSelectedIds(new Set());
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  const openCreateModal = () => {
    setEditingRoomId(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setEditingRoomId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRoomId(undefined);
  };

  if (tab === 'floors') {
    return (
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-1">
            <button
              onClick={() => setTab('rooms')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-text-muted hover:text-text hover:bg-background"
            >
              <DoorOpen size={16} />
              Rooms
            </button>
            <button
              onClick={() => setTab('floors')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-primary text-white shadow-sm"
            >
              <Building size={16} />
              Floors
            </button>
          </div>
        </div>
        <Floors />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs + Add Room */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-1">
          <button
            onClick={() => setTab('rooms')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === 'rooms'
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <DoorOpen size={16} />
            Rooms
          </button>
          <button
            onClick={() => setTab('floors')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === 'floors'
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <Building size={16} />
            Floors
          </button>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
        >
          <Plus size={16} />
          Add Room
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search rooms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={requestBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load diagnostic rooms</span>
        </div>
      )}

      {!isLoading && !error && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={rooms.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Floor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rooms.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>No rooms match your search.{' '}
                          <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No diagnostic rooms found. Add your first room to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  rooms.map((room: any) => (
                    <tr key={room.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(room.id)}
                          onChange={() => toggleOne(room.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-text">
                        <button
                          onClick={() => setDetailModal(room)}
                          className="hover:text-primary hover:underline transition-colors text-left"
                        >
                          {room.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{room.floor?.name || '—'}</td>
                      <td className="px-4 py-3 text-text-muted max-w-xs truncate">{room.description || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            room.status === 'ACTIVE'
                              ? 'bg-success/10 text-success'
                              : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {room.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {room.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(room.id)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: room.id, label: room.name })}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="rooms"
          />
        </div>
      )}

      <DiagnosticRoomForm
        isOpen={isModalOpen}
        roomId={editingRoomId}
        onClose={closeModal}
        onSuccess={() => {}}
      />

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <DoorOpen className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">{detailModal.name}</h2>
              </div>
              <button
                onClick={() => setDetailModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {detailModal.floor && (
                <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Floor</p>
                    <p className="text-sm font-medium text-text">{detailModal.floor.name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Description</p>
                  <p className="text-sm font-medium text-text">{detailModal.description || '—'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  detailModal.status === 'ACTIVE'
                    ? 'bg-success/10 text-success'
                    : 'bg-danger/10 text-danger'
                }`}>
                  {detailModal.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  {detailModal.status}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
              <button
                onClick={() => setDetailModal(null)}
                className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setDetailModal(null);
                  openEditModal(detailModal.id);
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
              >
                <Pencil size={14} />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete room?"
        description={
          <>
            This will delete <strong className="text-text">{deleteTarget?.label}</strong>. This
            cannot be undone.
          </>
        }
        confirmLabel="Delete room"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} room${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected rooms. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} room${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
